/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useImpostazioni, urlImmagine } from '../hooks/useImpostazioni';
import { notificheAPI } from '../api/notifiche';
import { Home, ClipboardList, Bell, LogOut, MessageSquarePlus } from 'lucide-react';

const ACCENT = '#10B981';

// Gli avvisi stanno nella barra in basso e non in una tendina nell'intestazione come
// nelle altre due app: questa si usa dal telefono, con una mano, e il pollice arriva
// qui. Se l'amministratore chiude o annulla un timer, il tecnico deve accorgersene
// senza andarlo a cercare.
const NAV = [
  { to: '/dashboard', label: 'Oggi', icon: Home },
  { to: '/interventi', label: 'Lavori', icon: ClipboardList },
  { to: '/notifiche', label: 'Avvisi', icon: Bell, badge: true },
  { to: '/segnalazioni', label: 'Segnala', icon: MessageSquarePlus },
];

export default function TecnicoLayout({ children, title }) {
  const { user, logout } = useAuth();
  const impostazioni = useImpostazioni();
  const logo = urlImmagine(impostazioni.app_logo_url);
  const location = useLocation();

  // Ricontrollato ogni minuto: un timer chiuso dall'amministratore mentre il tecnico
  // ha l'app aperta deve comparire da solo, senza ricaricare la pagina.
  const { data: notifiche = [] } = useQuery({
    queryKey: ['notifiche'],
    queryFn: () => notificheAPI.lista(),
    refetchInterval: 60000,
    enabled: Boolean(user),
  });
  const nonLette = notifiche.filter((n) => !n.letta).length;
  return (
    <div style={styles.wrapper}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brand}>
            {logo
              ? <img src={logo} alt="" style={styles.brandLogo} />
              : <div style={{ ...styles.brandDot, backgroundColor: ACCENT }} />}
            <div>
              <div style={styles.brandTitle}>{impostazioni.app_nome}</div>
              <div style={styles.brandSub}>Area Tecnico</div>
            </div>
          </div>
          <div style={styles.userArea}>
            <span style={styles.userName}>{user?.nome} {user?.cognome}</span>
            <button onClick={logout} style={styles.logoutBtn} title="Esci"><LogOut size={18} /></button>
          </div>
        </div>
      </header>
      <main style={styles.main}>
        {title && <h1 style={styles.pageTitle}>{title}</h1>}
        {children}
        <div style={styles.footer}>
          <Link to="/privacy" style={styles.footerLink}>Informativa Privacy</Link>
        </div>
      </main>
      <nav style={styles.bottomNav}>
        {NAV.map(({ to, label, icon: Icon, badge }) => {
          const active = location.pathname === to || (to !== '/dashboard' && location.pathname.startsWith(to));
          return (
            <Link key={to} to={to} style={{ ...styles.navItem, ...(active ? { color: ACCENT } : { color: '#6B7280' }) }}>
              <span style={styles.navIcona}>
                <Icon size={22} />
                {badge && nonLette > 0 && (
                  <span style={styles.badge}>{nonLette > 9 ? '9+' : nonLette}</span>
                )}
              </span>
              <span style={styles.navLabel}>{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

const styles = {
  wrapper: { minHeight: '100vh', backgroundColor: '#F9FAFB', paddingBottom: '70px' },
  header: { backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0, zIndex: 20 },
  headerInner: { maxWidth: '800px', margin: '0 auto', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  brandDot: { width: '12px', height: '12px', borderRadius: '4px' },
  brandLogo: { width: '28px', height: '28px', objectFit: 'contain', flexShrink: 0 },
  brandTitle: { fontWeight: 700, fontSize: '0.95rem' },
  brandSub: { fontSize: '0.72rem', color: '#6B7280' },
  userArea: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  userName: { fontSize: '0.88rem', color: '#374151', fontWeight: 500 },
  logoutBtn: { padding: '0.45rem', backgroundColor: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex' },
  main: { maxWidth: '800px', margin: '0 auto', padding: '1.25rem' },
  pageTitle: { fontSize: '1.4rem', fontWeight: 700, margin: '0 0 1.25rem' },
  footer: { marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB', textAlign: 'center' },
  footerLink: { color: '#9CA3AF', fontSize: '0.8rem', textDecoration: 'none' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: 'white', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-around', padding: '0.5rem 0', zIndex: 20 },
  navItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', textDecoration: 'none', padding: '0.3rem 1.25rem' },
  navIcona: { position: 'relative', display: 'inline-flex' },
  badge: { position: 'absolute', top: '-5px', right: '-8px', backgroundColor: '#EF4444', color: 'white', fontSize: '0.62rem', fontWeight: 700, minWidth: '16px', height: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' },
  navLabel: { fontSize: '0.72rem', fontWeight: 600 },
};
